import { useToastContext } from '@payaca/ambrosia';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { extractErrorMessage } from '@/api/extractErrorMessage';
import { actions as usersActions } from '@/api/users';
import { useFormState } from '@/hooks/useFormState';
import { useAccount } from '@/api/rest/users/useAccount';

type FieldValidator = {
  validate: (x: any) => boolean;
  error: string;
};

const saveErrorMessage = (err: unknown): string => {
  const { message, code } = extractErrorMessage(err);
  return code === 'BUSINESS_VALIDATION_FAILED'
    ? message
    : `We couldn't save your changes`;
};

export const useUpdateBusinessAccount = () => {
  const dispatch = useDispatch();
  const account = useAccount();

  return useMemo(
    () =>
      async <TState extends object>(state: TState) =>
        new Promise<void>((resolve, reject) => {
          dispatch(
            usersActions.updateBusinessAccount(
              account.id,
              state,
              (err: unknown) => (err ? reject(err) : resolve())
            )
          );
        }),
    [dispatch, account.id]
  );
};

/**
 * Form state for a slice of the current business account. Saving is manual —
 * the returned `saveAndFlush` is wired to a Save button; nothing persists until
 * it is pressed. Each settings sub-form owns a disjoint slice of account fields,
 * and `updateBusinessAccount` merges the partial it is given.
 *
 * NOTE: this deliberately extends the legacy `useFormState` + `@payaca/components`
 * field stack because the settings sub-forms it serves relocate existing fields.
 * It is not the house form pattern — new forms should use Ambrosia `Form` with
 * react-hook-form (see `apps/webapp-provider/AGENTS.md`). The settings rebuild
 * onto that stack is planned; do not spread this hook to new surfaces.
 */
export const useBusinessAccountForm = <TFormState extends object>(
  initialState: TFormState,
  fieldValidators: Partial<Record<keyof TFormState, Array<FieldValidator>>>,
  save?: (state: TFormState) => Promise<void>
) => {
  const updateBusinessAccount = useUpdateBusinessAccount();
  const { pushSuccess, pushError } = useToastContext();
  const saveState = save ?? updateBusinessAccount;

  const saveFormData = useMemo(
    () => async (state: TFormState) => {
      try {
        await saveState(state);
      } catch (err) {
        pushError(saveErrorMessage(err));
        throw err;
      }
      pushSuccess('Your changes have been saved');
    },
    [saveState, pushSuccess, pushError]
  );

  return useFormState<
    TFormState,
    Partial<Record<keyof TFormState, FieldValidationResult>>
  >(initialState, fieldValidators, {
    autoSaveFn: saveFormData,
    manualSaveOnly: true,
  });
};
